import { useEffect, useRef } from 'react';
import { useStore } from 'react-redux';
import {
  clearCookieYesReturnState,
  isCookieYesDisabled,
  readCookieYesReturnState,
  saveCookieYesReturnState,
} from '../utils';
import { selectFlow } from '../store/app';
import { selectLanding } from '../store/plans';
import type { RootState } from '../store';

const resolveCookieYesDisabled = (state: RootState): boolean => {
  const flow = selectFlow(state);
  // A new tab (e.g. a return from a payment provider) starts with an empty store
  const returnState = !flow?.length ? readCookieYesReturnState() : null;
  const source = returnState ?? { landing: selectLanding(state), flow };

  return isCookieYesDisabled(window.location.pathname, source.landing, source.flow);
};

// Decided once on load: a changed answer would make useInlineScript inject GTM a second time
export const useCookieYesDisabled = (): boolean => {
  const store = useStore<RootState>();
  const isDisabled = useRef<boolean | null>(null);

  if (isDisabled.current === null) {
    isDisabled.current = resolveCookieYesDisabled(store.getState());
  }

  useEffect(() => {
    // Saved when the tab is left (e.g. for a payment provider), so that a return into a new tab
    // still knows the landing. The tab left last wins, as the return most likely belongs to it.
    const saveOnLeave = () => {
      const state = store.getState();
      const flow = selectFlow(state);
      if (!flow?.length) return;

      if (isDisabled.current) saveCookieYesReturnState(selectLanding(state), flow);
      else clearCookieYesReturnState();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') saveOnLeave();
    };

    window.addEventListener('pagehide', saveOnLeave);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', saveOnLeave);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [store]);

  return isDisabled.current;
};
